import { PUBLIC_SUPPORT_EMAIL } from '../../../shared/config/constants'
import { t } from '../../../shared/config/translations'
import type { ClientUser } from '../../../shared/types/user'
import { infoTooltipTemplate } from '../../components/infoTooltipTemplates'
import { renderSwitch } from '../../components/inputs/switchTemplates'
import {
	privacyPolicyContentTemplate,
	termsOfServiceContentTemplate,
} from '../../components/legalContentTemplates'
import { hideDurationOptionsTemplate } from '../../components/release/releaseDialogOptionTemplates'
import { releaseNotesOverviewTemplate } from '../../components/releaseNotesTemplates'
import { IS_NATIVE_APP } from '../../config/environment'
import { readHapticsPreference } from '../../utils/haptics'

export const SETTINGS_HELP_OVERLAY_KEYS = [
	'contact-feedback',
	'report-bug',
	'whats-new',
	'privacy-policy',
	'terms-service',
] as const

export type SettingsHelpOverlayKey = (typeof SETTINGS_HELP_OVERLAY_KEYS)[number]

export function settingsPageTemplate(): string {
	return `
        <div id="settings-section">
            <header class="page-header">
                <h1 id="page-title">${t('settings.title')}</h1>
            </header>
            <form id="settings-form" class="settings-form">
                <div class="settings-group">
                    ${settingsLinkRowTemplate(
						t('settings.appSettings'),
						'data-app-settings-overlay',
					)}
                </div>

                <h3>${t('settings.historyData')}</h3>
                <div class="settings-group">
                    ${settingsHistoryFileRowTemplate({
						buttonId: 'settings-history-import-btn',
						buttonText: t('settings.historyImportAction'),
						direction: 'up',
						extraHtml: `<input type="file" id="settings-history-import-input" accept=".csv,text/csv" hidden />`,
						label: t('settings.historyImportLabel'),
					})}
                    ${settingsHistoryFileRowTemplate({
						buttonId: 'settings-history-export-btn',
						buttonText: t('settings.historyExportButton'),
						direction: 'down',
						label: t('settings.historyExportLabel'),
					})}
                    ${settingsHistoryFileRowTemplate({
						buttonId: 'settings-history-sample-btn',
						buttonText: t('settings.historySampleButton'),
						direction: 'down',
						label: t('settings.historySampleLabel'),
					})}
                </div>

                <h3>${t('settings.helpAbout')}</h3>
                <div class="settings-group">
                    ${settingsHelpRowTemplate(
						'contact-feedback',
						t('settings.contactFeedback'),
					)}
                    ${settingsHelpRowTemplate(
						'report-bug',
						t('settings.reportBug'),
					)}
                    ${settingsHelpRowTemplate(
						'whats-new',
						t('settings.whatsNew'),
					)}
                    ${settingsHelpRowTemplate(
						'privacy-policy',
						t('settings.privacyPolicy'),
					)}
                    ${settingsHelpRowTemplate(
						'terms-service',
						t('settings.termsService'),
					)}
                </div>

                <h3>${t('settings.account')}</h3>
                <div class="settings-group">
                    <div>
                        <span class="settings-action-label">${t('settings.logout')}</span>
                        <button type="button" id="settings-logout-btn" class="btn secondary small">
                            ${t('settings.logout')}
                        </button>
                    </div>
                    <div class="settings-reset-history-row">
                        <span class="settings-info-label"><span class="settings-action-label">${t('settings.resetHistoryLabel')}</span>${infoTooltipTemplate('settings-reset-history-help-text', t('settings.resetHistoryHelpLabel'), t('settings.resetHistoryHelp'))}</span>
                        <button type="button" id="settings-reset-history-btn" class="btn danger outlined small">
                            ${t('settings.resetHistory')}
                        </button>
                    </div>
                    <div>
                        <span class="settings-action-label">${t('settings.deleteAccountLabel')}</span>
                        <button type="button" id="settings-delete-btn" class="btn danger outlined small">
                            ${t('settings.deleteAccount')}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    `
}

type SettingsHistoryFileRowOptions = {
	buttonId: string
	buttonText: string
	direction: 'down' | 'up'
	extraHtml?: string
	label: string
}

function settingsHistoryFileRowTemplate({
	buttonId,
	buttonText,
	direction,
	extraHtml = '',
	label,
}: SettingsHistoryFileRowOptions): string {
	return `
        <div>
            <span class="settings-action-label">${label}</span>
            <button
                type="button"
                id="${buttonId}"
                class="btn secondary small settings-history-file-button"
            >
                <p>${buttonText}</p>
                ${historyFileIconTemplate(direction)}
            </button>
            ${extraHtml}
        </div>
    `
}

function historyFileIconTemplate(direction: 'down' | 'up'): string {
	const arrow =
		direction === 'up'
			? '<path d="M12 12v6"/><path d="m15 15-3-3-3 3"/>'
			: '<path d="M12 18v-6"/><path d="m9 15 3 3 3-3"/>'
	return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-file-${direction}-icon lucide-file-${direction}"><path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z"/><path d="M14 2v5a1 1 0 0 0 1 1h5"/>${arrow}</svg>`
}

export function settingsAppOverlayTemplate(user: ClientUser): string {
	const settings = user.settings

	return settingsOverlayShellTemplate(
		t('settings.appSettings'),
		`
            <form class="settings-form" data-app-settings-form>
                <h3>${t('settings.general')}</h3>
                <div class="settings-group">
                    <div>
                        <label for="homepage-select">${t('settings.homepage')}</label>
                        <div class="settings-select-control">
                            <select id="homepage-select">
                                <option value="/shuffle" ${settings?.homepage === '/shuffle' ? 'selected' : ''}>${t('settings.homepageShuffle')}</option>
                                <option value="/collection" ${settings?.homepage === '/collection' ? 'selected' : ''}>${t('settings.homepageCollection')}</option>
                            </select>
                            <svg><use href="#symbols-arrow-down"></use></svg>
                        </div>
                    </div>
                    ${renderSwitch({
						checked: settings?.theme !== 'dark',
						'data-off': 'light',
						'data-on': 'dark',
						id: 'theme-toggle',
						label: t('settings.lightMode'),
					})}
                    ${
						IS_NATIVE_APP
							? renderSwitch({
									checked: readHapticsPreference(),
									id: 'haptics-toggle',
									label: t('settings.haptics'),
								})
							: ''
					}
                </div>

                <h3>${t('settings.collection')}</h3>
                <div class="settings-group">
                    ${renderSwitch({
						checked: settings?.showCollectionValue !== false,
						id: 'show-collection-value-toggle',
						label: t('settings.showCollectionValue'),
					})}
                </div>

                <h3>${t('settings.shuffle')}</h3>
                <div class="settings-group">
                    ${renderSwitch({
						checked: !!settings?.skipShuffleAnimation,
						id: 'skip-shuffle-animation-toggle',
						label: t('settings.skipShuffleAnimation'),
					})}
                </div>

                <h3>${t('settings.history')}</h3>
                <div class="settings-group">
                    <div class="settings-group-item">
                        <div>
                            <label for="hide-duration-select">${t('settings.defaultHide')}</label>
                            <div class="settings-select-control">
                                <select id="hide-duration-select">
                                    ${hideDurationOptionsTemplate(settings?.defaultHideDurationMonths === -1 ? 'forever' : (settings?.defaultHideDurationMonths ?? null), { foreverValue: '-1' })}
                                </select>
                                <svg><use href="#symbols-arrow-down"></use></svg>
                            </div>
                        </div>
                        <p class="f secondary">${t('settings.defaultHideNote')}</p>
                    </div>
                </div>

				<button
					type="button"
					class="settings-app-feedback-prompt"
					data-app-settings-feedback
				>
					<span>${t('settings.appSettingsFeedbackPrompt')}</span>
					<span>${t('settings.appSettingsFeedbackAction')}</span>
				</button>
            </form>
        `,
	)
}

function settingsHelpRowTemplate(
	overlayKey: SettingsHelpOverlayKey,
	label: string,
): string {
	return settingsLinkRowTemplate(
		label,
		`data-settings-overlay="${overlayKey}"`,
	)
}

function settingsLinkRowTemplate(label: string, dataAttribute: string): string {
	return `
        <div class="settings-link-row">
            <button
                type="button"
                class="settings-link-button"
                ${dataAttribute}
            >
                <span>${label}</span>
	                <svg><use href="#symbols-arrow-forward"></use></svg>
            </button>
        </div>
    `
}

export function settingsHelpOverlayTemplate(
	overlayKey: SettingsHelpOverlayKey,
): string {
	switch (overlayKey) {
		case 'contact-feedback':
			return settingsOverlayShellTemplate(
				t('settings.contactFeedback'),
				`
                    <p class="f secondary">${t('settings.contactFeedbackIntro')}</p>
                    ${supportEmailAlternativeTemplate()}
                    <form class="settings-overlay-form" data-settings-overlay-form data-support-kind="contact-feedback">
                        <label class="settings-overlay-field" for="settings-contact-email">
                            <span>${t('settings.email')} <span class="settings-required-marker" aria-hidden="true">*</span></span>
                            <input
                                class="tt-input"
                                id="settings-contact-email"
                                name="email"
                                type="email"
                                autocomplete="email"
                                maxlength="254"
                                placeholder="${t('settings.emailPlaceholder')}"
                            />
                        </label>
                        <label class="settings-overlay-field" for="settings-contact-message">
                            <span>${t('settings.message')} <span class="settings-required-marker" aria-hidden="true">*</span></span>
                            <textarea
                                class="tt-input"
                                id="settings-contact-message"
                                name="message"
                                rows="6"
                                maxlength="5000"
                                placeholder="${t('settings.contactFeedbackPlaceholder')}"
                                required
                            ></textarea>
                        </label>
                        ${supportCopyOptionTemplate()}
                        ${settingsOverlayFormActionsTemplate(t('settings.sendMessage'))}
                    </form>
                `,
			)
		case 'report-bug':
			return settingsOverlayShellTemplate(
				t('settings.reportBug'),
				`
                    <p class="f secondary">${t('settings.reportBugIntro')}</p>
                    ${supportEmailAlternativeTemplate()}
                    <form class="settings-overlay-form" data-settings-overlay-form data-support-kind="report-bug">
                        <label class="settings-overlay-field" for="settings-bug-email">
                            <span>${t('settings.email')} <span class="settings-required-marker" aria-hidden="true">*</span></span>
                            <input
                                class="tt-input"
                                id="settings-bug-email"
                                name="email"
                                type="email"
                                autocomplete="email"
                                maxlength="254"
                                placeholder="${t('settings.emailPlaceholder')}"
                            />
                        </label>
                        <label class="settings-overlay-field" for="settings-bug-report">
                            <span>${t('settings.bugDescription')} <span class="settings-required-marker" aria-hidden="true">*</span></span>
                            <textarea
                                class="tt-input"
                                id="settings-bug-report"
                                name="message"
                                rows="8"
                                maxlength="5000"
                                placeholder="${t('settings.bugReportPlaceholder')}"
                                required
                            ></textarea>
                        </label>
                        <p class="f secondary">${t('settings.bugReportContextNotice')}</p>
                        ${supportCopyOptionTemplate()}
                        ${settingsOverlayFormActionsTemplate(t('settings.submitBugReport'))}
                    </form>
                `,
			)
		case 'whats-new':
			return settingsOverlayShellTemplate(
				t('settings.whatsNew'),
				releaseNotesOverviewTemplate(),
			)
		case 'privacy-policy':
			return settingsOverlayShellTemplate(
				t('settings.privacyPolicy'),
				privacyPolicyContentTemplate(),
			)
		case 'terms-service':
			return settingsOverlayShellTemplate(
				t('settings.termsService'),
				termsOfServiceContentTemplate(),
			)
	}
}

function settingsOverlayShellTemplate(title: string, content: string): string {
	return `
        <section class="settings-help-overlay">
            <h1>${title}</h1>
            ${content}
        </section>
    `
}

function settingsOverlayFormActionsTemplate(buttonLabel: string): string {
	return `
        <div class="settings-overlay-actions">
            <button type="submit" class="btn primary">${buttonLabel}</button>
        </div>
    `
}

function supportCopyOptionTemplate(): string {
	return `<div class="settings-support-copy">
        ${renderSwitch({
			id: 'support-copy-toggle',
			name: 'sendCopy',
			label: t('settings.sendCopy'),
		})}
    </div>`
}

function supportEmailAlternativeTemplate(): string {
	return `<p class="f secondary">${t('settings.emailAlternative')} <a href="mailto:${PUBLIC_SUPPORT_EMAIL}">${PUBLIC_SUPPORT_EMAIL}</a>.</p>`
}
